/**
 * Former Firebase Remote Config key: `analytics_config`.
 * Captured from the live app (`window.analyticsConfig`) to preserve current behavior.
 */
export interface AnalyticsEventConfig {
  enabled: boolean;
  parameters?: string[];
}

export interface AnalyticsConfig {
  analytics: {
    enabled: boolean;
    providers: string[];
    events: Record<string, AnalyticsEventConfig>;
  };
}

export const analyticsConfig: AnalyticsConfig = {
  analytics: {
    enabled: false,
    providers: ['GTM'],
    events: {
      sign_up: {
        enabled: true,
        parameters: ['method', 'email', 'first_name', 'last_name', 'phone', 'country', 'campaign', 'userid_no', 'device_type'],
      },
      sign_up_error: {
        enabled: true,
        parameters: ['error_message', 'step', 'userid_no', 'device_type'],
      },
      login_success: {
        enabled: true,
        parameters: ['provider', 'method', 'userid_no', 'device_type'],
      },
      login_failure: {
        enabled: true,
        parameters: ['email', 'error_message', 'userid_no', 'device_type'],
      },
      signup_prompt_shown: {
        enabled: true,
        parameters: ['asset_id', 'title', 'userid_no', 'device_type'],
      },
      begin_checkout: {
        enabled: false,
        parameters: ['value', 'currency', 'items', 'userid_no', 'device_type'],
      },
      add_payment_info: {
        enabled: true,
        parameters: ['payment_type', 'value', 'currency', 'userid_no', 'device_type'],
      },
      add_payment_info_start: {
        enabled: true,
        parameters: ['userid_no', 'device_type'],
      },
      add_payment_info_error: {
        enabled: true,
        parameters: ['error_message', 'userid_no', 'device_type'],
      },
      payment_initiated: {
        enabled: true,
        parameters: ['asset_id', 'price', 'value', 'currency', 'items', 'method', 'provider', 'userid_no', 'device_type'],
      },
      checkout_loaded: {
        enabled: true,
        parameters: ['email', 'price', 'currency', 'package'],
      },
      payment_success: {
        enabled: true,
        parameters: ['asset_id', 'price', 'transaction_id', 'value', 'currency', 'items', 'coupon', 'userid_no', 'device_type'],
      },
      payment_failure: {
        enabled: true,
        parameters: ['asset_id', 'provider', 'error_code', 'error_message', 'payment_method', 'userid_no', 'device_type'],
      },
      purchase: {
        enabled: false,
        parameters: ['transaction_id', 'value', 'currency', 'listing_value', 'coupon', 'items', 'userid_no', 'device_type'],
      },
      subscription_cancelled: {
        enabled: true,
        parameters: ['reason', 'plan_id', 'plan_name', 'userid_no', 'device_type'],
      },
      access_granted: {
        enabled: true,
        parameters: ['asset_id', 'access_type', 'plan_name', 'userid_no', 'device_type'],
      },
      paywall_view: {
        enabled: false,
        parameters: ['asset_id', 'title', 'price', 'currency', 'provider', 'userid_no', 'device_type'],
      },
      video_play: {
        enabled: true,
        parameters: ['video_id', 'title', 'duration', 'content_type', 'autoplay', 'video_progress', 'userid_no', 'device_type'],
      },
      video_pause: {
        enabled: true,
        parameters: ['video_id', 'title', 'video_progress', 'content_type', 'userid_no', 'device_type'],
      },
      video_progress: {
        enabled: false,
        parameters: ['video_id', 'title', 'video_progress', 'duration', 'content_type', 'userid_no', 'device_type'],
      },
      video_complete: {
        enabled: false,
        parameters: ['video_id', 'title', 'duration', 'watched_duration', 'watched_percentage', 'userid_no', 'content_type', 'device_type'],
      },
      video_error: {
        enabled: false,
        parameters: ['video_id', 'title', 'error_code', 'error_message', 'content_type', 'userid_no', 'device_type'],
      },
      video_forward: {
        enabled: false,
        parameters: ['video_id', 'title', 'content_type', 'from_position', 'to_position', 'userid_no', 'device_type'],
      },
      video_rewind: {
        enabled: false,
        parameters: ['video_id', 'title', 'content_type', 'from_position', 'to_position', 'userid_no', 'device_type'],
      },
      video_buffer_start: {
        enabled: false,
        parameters: ['video_id', 'title', 'position', 'content_type', 'userid_no', 'device_type'],
      },
      video_buffer_end: {
        enabled: false,
        parameters: ['video_id', 'title', 'position', 'content_type', 'buffer_duration', 'userid_no', 'device_type'],
      },
      video_quality_change: {
        enabled: false,
        parameters: ['video_id', 'title', 'old_quality', 'from_quality', 'to_quality', 'content_type', 'userid_no', 'device_type'],
      },
      video_mute_toggle: {
        enabled: false,
        parameters: ['video_id', 'title', 'is_muted', 'content_type', 'userid_no', 'device_type'],
      },
      video_fullscreen: {
        enabled: false,
        parameters: ['video_id', 'title', 'content_type', 'userid_no', 'device_type'],
      },
      video_exit_fullscreen: {
        enabled: false,
        parameters: ['video_id', 'title', 'content_type', 'userid_no', 'device_type'],
      },
      search_performed: {
        enabled: false,
        parameters: ['query', 'result_count', 'userid_no', 'device_type'],
      },
      category_filter_applied: {
        enabled: false,
        parameters: ['filter_name', 'filter_value', 'userid_no', 'device_type'],
      },
      share_clicked: {
        enabled: false,
        parameters: ['video_id', 'title', 'platform', 'share_medium', 'content_id', 'content_type', 'userid_no', 'device_type'],
      },
      recommendation_clicked: {
        enabled: false,
        parameters: ['from_video_id', 'to_video_id', 'position', 'userid_no', 'device_type'],
      },
      carousel_scroll: {
        enabled: false,
        parameters: ['carousel_id', 'scroll_direction', 'userid_no', 'device_type'],
      },
      scroll_horizontal: {
        enabled: false,
        parameters: ['scroll_direction', 'userid_no', 'device_type'],
      },
      scroll: {
        enabled: false,
        parameters: ['scroll_direction', 'percent_scrolled', 'userid_no', 'device_type'],
      },
      screen_view: {
        enabled: true,
        parameters: ['firebase_screen_class', 'firebase_screen', 'playlist_name', 'userid_no', 'device_type'],
      },
      add_to_wishlist: {
        enabled: false,
        parameters: ['content_id', 'content_type', 'userid_no', 'device_type'],
      },
    },
  },
};
